import { createCookie } from '@app/utils/cookies'
import { createSelector } from '@app/utils/createSelector'
import moment from '@app/utils/moment'
import { DEFAULT_CURRENCY } from '@app/utils/printCurrency'

import { withAbortSignal } from '@app/packages/abortContext/actions'
import type { ApiCurrency } from '@app/packages/api/v2/types/entities'
import { createPromise } from '@app/packages/thunkSelector/promiseState'
import { createLoadable } from '@app/packages/thunkSelector/thunkSelector'

import { getAbortControllerService } from '@app/store/actions/contexts/abortControllerService'
import { getCookies } from '@app/store/actions/contexts/cookies'
import { createReduxSlice } from '@app/store/redux_slice'
import { profileUserResultSelector } from '@app/store/selectors/profile'
import { createThunk } from '@app/store/thunk'

import { profileUserSelector } from './profile'

const currencyCodeSlice = createReduxSlice<{ code: string }>('currency_code')

export const currencyCodeResultSelector = createSelector([currencyCodeSlice.selector, profileUserResultSelector], (slice, profileResult) =>
  createLoadable(c => {
    const profile = c.unwrap(profileResult)
    if (profile && 'display_currency' in profile && profile.display_currency) {
      return profile.display_currency
    }
    if (!slice) {
      return c.load(
        createPromise('Currency code must be set', 'currency_code', async dispatch => {
          const cookies = dispatch(getCookies())
          dispatch(currencyCodeSlice.set({ code: await currencyCookie.get(cookies) }))
        })
      )
    }
    return slice.code ?? DEFAULT_CURRENCY
  })
)

export function changeCurrency(currency: ApiCurrency, persist = false) {
  return createThunk(async (dispatch, getState) => {
    const abort = dispatch(getAbortControllerService())
    const cookies = dispatch(getCookies())
    const abortController = abort.createAbortController('currency-change')
    return dispatch(
      withAbortSignal(abortController.signal, async dispatch => {
        const { updateProfile } = await import('@app/store/actions/profile')
        const code = currency.attributes.iso_code
        if (persist) {
          await currencyCookie.set(cookies, code)
          const user = profileUserSelector(getState())
          if (user && user.account_type !== 'visitor') {
            await dispatch(updateProfile({ display_currency: code }))
          }
        }

        dispatch(currencyCodeSlice.set({ code }))
      })
    )
  })
}

const currencyCookie = createCookie<string>(
  'kidosut__currency',
  value => value,
  value => value,
  () => ({ expires: moment().add(1, 'year') })
)
